/**
 * Reading a prerequisite (concept-map edge) patch, on the browser side.
 *
 * A lesson's `prerequisites` is a map of *lesson id* → *weight* (`prereq_theta`), e.g.
 * `{"1448": 1}`, meaning a learner must finish lesson 1448 first. Brain2 builds the learner course
 * by dropping every published lesson whose prerequisites are not themselves published, and the drop
 * cascades — which is why editing this map is a consequential act and not a metadata tweak.
 */

/**
 * MIRRORS `isEdgeOnlyPatch` in `packages/api/src/workflow/release.service.ts`, which is the
 * authority: a patch carrying `prerequisites` and NONE of these keys is routed to the edge-only
 * write, and one carrying any of them goes through the whole-document content save instead.
 *
 * It is duplicated rather than shared because the API and the web are separate workspaces with no
 * shared package between them. `prerequisitePatch.test.ts` pins this list so the copy cannot drift
 * silently — if the executor's list changes, that test is the thing that has to be updated with it.
 *
 * The consequence of getting it wrong is concrete: injecting a `title` into a prerequisite patch
 * flips the executor onto the whole-document path and its content validator, which refuses most
 * published lessons. The disabled title input used to be the only thing preventing that.
 */
export const EDGE_ONLY_EXCLUDED_KEYS = [
  'steps',
  'responses',
  'summary',
  'rc_text',
  'title',
  'options',
  'max_steps',
  'step_counter',
  'response_counter',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True when `prerequisites` is the patch's ONLY key.
 *
 * **Strictly narrower than the executor's `isEdgeOnlyPatch`, and deliberately so.** That predicate
 * answers "does this route to the edge-only write?", which allows other non-content keys to ride
 * along (`group_id`, `published`, anything a producer emits). This one answers "can this editor
 * express the whole patch?" — and it cannot: it submits `prerequisites` and nothing else, so a
 * patch carrying a second key would have that key silently dropped from the reviewer's submission.
 * Such an item falls through to the title input, exactly as it did before this editor existed.
 * Cross-model review of the diff, major 5.
 */
export function isPrerequisiteOnlyPatch(patch: unknown): patch is Record<string, unknown> {
  if (!isRecord(patch) || !('prerequisites' in patch)) {
    return false;
  }
  return Object.keys(patch).length === 1;
}

export interface PrerequisiteTuple {
  /**
   * The map the item was REVIEWED AGAINST. It is carried through a reviewer's edit verbatim and is
   * never recomputed from what the browser last fetched: the executor turns it into a conditional
   * filter (`content/prerequisite-expectation.ts`), and a recomputed `before` would assert only that
   * this tab is consistent with itself.
   */
  before: unknown;
  after: unknown;
}

/** The `{before, after}` tuple, or null for a patch stating only the new map. */
export function readPrerequisiteTuple(patch: unknown): PrerequisiteTuple | null {
  if (!isRecord(patch) || !isRecord(patch.prerequisites)) {
    return null;
  }
  const value = patch.prerequisites;
  if (!('before' in value) || !('after' in value)) {
    return null;
  }
  return { before: value.before, after: value.after };
}

/** The prerequisite map a patch proposes, whether it states a tuple or a bare map. */
export function proposedPrerequisites(patch: unknown): Record<string, unknown> {
  const tuple = readPrerequisiteTuple(patch);
  const value = tuple ? tuple.after : isRecord(patch) ? patch.prerequisites : undefined;
  return isRecord(value) ? { ...value } : {};
}

/**
 * The patch a reviewer's edit submits — `prerequisites` and NOTHING else.
 *
 * Never spread the item's own patch into this: a key that changes `isEdgeOnlyPatch`'s answer routes
 * the write somewhere else entirely.
 */
export function buildPrerequisiteReviewerPatch(
  patch: unknown,
  edited: Record<string, unknown>,
): Record<string, unknown> {
  const tuple = readPrerequisiteTuple(patch);
  if (!tuple) {
    // The item stated no `before`, so there is nothing to assert and inventing one would be a lie.
    // The write behaves exactly as it did before the assertion existed.
    return { prerequisites: edited };
  }
  return { prerequisites: { before: tuple.before, after: edited } };
}
